import { useRef } from 'react';

import { Alert, Flex } from '@zillow/constellation';
import {
  AdornedInput,
  Anchor,
  Box,
  Combobox,
  FormField,
  FormHelp,
  Input,
  Label,
  Select,
  Text,
} from '@zillow/constellation-10';

import { useSelector } from '../../../../../selectors/useSelector';

import { useAddressAutocomplete } from '../../../../../hooks/queries/address';

import accessUtils from '../../../../../utils/accessUtils';

import PropertyConstants from '../../../../../constants/PropertyConstants';
import AddressConstants from '../../../../../constants/AddressConstants';
import AddPropertyConstants from '../../../../../constants/AddPropertyConstants';

import RoomForRentCheckbox from '../../../../RoomForRentCheckbox';

import { useRefocusOnAddressInput, useRefocusOnUnitInput } from '../../hooks';

import type { ChangeEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { ListingErrorsReducerState, ListingReducerProps } from '../../../../../reducers/listing';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, MULTIPLEX, TOWNHOUSE },
} = PropertyConstants;

const MULTI_FAMILY_RESTRICTED_ERROR_MESSAGE =
  'Each unit at this address must be posted as a separate listing. Select the property type: Condo/apartment unit.';

const zillowAdvertisingURL = 'https://www.zillow.com/z/rental-property-advertising/';

type Props = {
  listing: ListingReducerProps;
  canAccessMultiplex?: boolean;
  contracts?: Array<{
    customerId: string;
    name: string;
  }>;
  errorObj: ListingErrorsReducerState;
  onListingChange: (keyName: keyof ListingReducerProps, newValue: string) => void;
  onCheckLocationRestriction: (fieldName?: keyof ListingReducerProps, fieldValue?: boolean | string) => Promise<void>;
  onCustomerSupportClick?: () => void;
  unitNumberDetected?: boolean;
  isValidatedWithoutUnitDetected?: boolean;
  propertyAlreadyPostedWarningMessage?: string | Array<JSX.Element | string>;
  isSubmitDisabled?: boolean;
  setIsSubmitDisabled: (arg: boolean) => void;
  behavior?: (typeof AddPropertyConstants.BEHAVIOR_OPTIONS)[keyof typeof AddPropertyConstants.BEHAVIOR_OPTIONS];
  isRefocusingAddressInput: boolean;
  setIsRefocusingAddressInput: (arg: boolean) => void;
};

const AddPropertyForm = ({
  behavior = AddPropertyConstants.BEHAVIOR_OPTIONS.ADD,
  listing,
  canAccessMultiplex = false,
  contracts = [],
  errorObj,
  unitNumberDetected = false,
  isValidatedWithoutUnitDetected = false,
  propertyAlreadyPostedWarningMessage = '',
  isSubmitDisabled = false,
  setIsSubmitDisabled,
  onListingChange,
  onCheckLocationRestriction,
  isRefocusingAddressInput,
  setIsRefocusingAddressInput,
}: Props) => {
  const { appConfig } = useSelector((state) => state.app);

  const { address, isRoomForRent, numUnits, propertyType, unit, customerId } = listing;
  const finalizedisRoomForRentValue = typeof isRoomForRent === 'boolean' ? isRoomForRent : isRoomForRent === 'true';

  const [setPartialAddress, queryData] = useAddressAutocomplete();
  const unitNumberInputElementRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  const isCommunityType = propertyType === COMMUNITY;
  const isMultiplexType = propertyType === MULTIPLEX;
  const isRoomForRentDisabled = isSubmitDisabled;
  const isDisabledForCommunityType = behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT && isCommunityType;
  const isDisabledForNonCommunityType = behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT && !isCommunityType;

  const addressFieldError = errorObj.address || (contracts.length <= 1 ? errorObj.customerId : null);

  useRefocusOnUnitInput(
    unitNumberDetected || isValidatedWithoutUnitDetected,
    isSubmitDisabled,
    unitNumberInputElementRef,
  );
  useRefocusOnAddressInput(isRefocusingAddressInput, setIsRefocusingAddressInput, addressInputRef);

  const canAccessNoWelcomePageML = accessUtils.canAccessNoWelcomePageML({ appConfig });

  const handleSelectAddress = async (option: ComboboxOptionType) => {
    const addressValue = typeof option === 'string' ? option : option.value;

    // Defer disabling to next tick to avoid interrupting Combobox's event handling
    setTimeout(() => {
      setIsSubmitDisabled(true);
    }, 0);

    onListingChange('address', addressValue);
    await onCheckLocationRestriction('address', addressValue);
    setIsSubmitDisabled(false);
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('address', e.target.value);

    setPartialAddress(e.target.value);
  };

  const handleUpdateMFNumUnits = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('numUnits', e.target.value);
  };

  const handleUpdateUnit = (e: ChangeEvent<HTMLInputElement>) => {
    const newUnit = e.target.value;
    if (newUnit.length <= AddressConstants.UNIT_NUMBER_LENGTH_LIMIT) {
      onListingChange('unit', newUnit);
    }
  };

  const handleUpdateUnitBlur = () => {
    onCheckLocationRestriction();
  };

  const handleToggleRoomForRent = () => {
    const stringCastedFlippedIsRoomForRentValue = String(!finalizedisRoomForRentValue);
    onListingChange('isRoomForRent', stringCastedFlippedIsRoomForRentValue);
    onCheckLocationRestriction('isRoomForRent', stringCastedFlippedIsRoomForRentValue);
  };

  const handleUpdatePropertyType = (value: string) => {
    onListingChange('propertyType', value);
    onCheckLocationRestriction('propertyType', value);
  };

  const handleUpdateCustomerId = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('customerId', e.target.value);
  };

  const renderUnitFormField = () => {
    switch (propertyType) {
      case COMMUNITY:
        return (
          <FormField css={{ marginTop: '300', marginBottom: '200' }} error={!!errorObj.numUnits}>
            <Label>Number of units</Label>
            <Input
              data-testid="number-of-units"
              error={!!errorObj.numUnits}
              onChange={handleUpdateMFNumUnits}
              onBlur={() => onCheckLocationRestriction()}
              value={numUnits}
              disabled={isSubmitDisabled}
            />
            <FormHelp error={!!errorObj.numUnits}>{errorObj.numUnits}</FormHelp>
          </FormField>
        );
      case MULTIPLEX:
        return (
          <FormField css={{ marginTop: '300', marginBottom: '200' }} error={!!errorObj.numUnits}>
            <Label>Number of units you own or manage</Label>
            <Input
              data-testid="number-of-units-multiplex"
              error={!!errorObj.numUnits}
              onChange={handleUpdateMFNumUnits}
              value={numUnits}
              disabled={isSubmitDisabled}
            />
            <FormHelp error={!!errorObj.numUnits}>{errorObj.numUnits}</FormHelp>
          </FormField>
        );
      default:
        return (
          <FormField css={{ marginBlock: '300' }} error={!!errorObj.unit}>
            <Label>
              Unit number
              {canAccessNoWelcomePageML && (
                <Text textStyle="body-sm" className="fs-unmask">
                  &nbsp;(if applicable)
                </Text>
              )}
            </Label>
            <AdornedInput
              startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
              input={
                <AdornedInput.Input
                  ref={unitNumberInputElementRef}
                  data-testid="unit-number"
                  aria-label="Unit number"
                  placeholder=""
                  onBlur={handleUpdateUnitBlur}
                  onChange={handleUpdateUnit}
                  value={unit || ''}
                  disabled={isSubmitDisabled}
                />
              }
            />
            <FormHelp error={!!errorObj.unit}>{errorObj.unit}</FormHelp>

            {!!errorObj.unit === false && (
              <FormHelp>
                {canAccessNoWelcomePageML
                  ? 'Enter apartment, suite, or unit number.'
                  : `If the property doesn't have a unit number, leave this field blank.`}
              </FormHelp>
            )}
          </FormField>
        );
    }
  };

  return (
    <div data-testid="AddPropertyForm">
      {Boolean(
        isValidatedWithoutUnitDetected || unitNumberDetected || Boolean(propertyAlreadyPostedWarningMessage),
      ) && (
        <Flex display="flex" flexDirection="column" gap="sm" marginTop="md">
          {isValidatedWithoutUnitDetected && (
            <Alert body="We are unable to validate the property address with this unit number. Please double-check before creating the property." />
          )}

          {unitNumberDetected && (
            <Alert body="We've detected this address may have a unit number. Please double-check before creating the property." />
          )}

          {Boolean(propertyAlreadyPostedWarningMessage) && <Alert body={propertyAlreadyPostedWarningMessage} />}
        </Flex>
      )}

      <FormField css={{ marginBlock: '300', marginRight: { md: '400' } }} error={!!errorObj.address}>
        <Label id="street-address-label">Street address</Label>
        <Combobox
          appearance="input"
          focusFirstOption={true}
          options={
            queryData?.data?.addresses?.map((address) => ({ ...address, value: address.name, label: address.name })) ??
            []
          }
          onClear={() => handleAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
          onOptionSelect={handleSelectAddress}
          selectCallbackFrequency="always"
          renderInput={({ ...rest }) => {
            return (
              <Combobox.Input
                {...rest}
                data-testid="autocomplete"
                ref={addressInputRef}
                value={address}
                disabled={isSubmitDisabled}
                onChange={handleAddressChange}
                aria-labelledby="street-address-label"
              />
            );
          }}
          renderEmptyState={() => (address as string).length <= 7 && null}
          freeForm={true}
          autoCompleteBehavior="none"
        />
        <FormHelp error={!!addressFieldError}>
          <div>{addressFieldError}</div>
        </FormHelp>

        {!!addressFieldError === false && (
          <FormHelp>
            Enter the USPS-validated address. You won’t be able to edit the address once you create the listing.
          </FormHelp>
        )}
      </FormField>

      <Box css={{ paddingRight: { md: '600' }, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '200' }}>
        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>
          <FormField css={{ marginBlock: '300' }} error={!!errorObj.propertyType}>
            <Label>Property type</Label>

            {/* TODO: if we remove this check we need to update cypress createListing test at canAccessMultiplex */}
            {canAccessMultiplex ? (
              <div data-testid="propertyType-select">
                <Combobox
                  appearance="select"
                  aria-label="Property type"
                  autoCompleteBehavior="automatic"
                  disabled={isSubmitDisabled}
                  fluidDropdown
                  limitChipsShown={1}
                  placeholder="Please select"
                  selectCallbackFrequency="change"
                  shouldAwaitInteractionResponse={true}
                  showLabelForValue={true}
                  size="md"
                  value={propertyType}
                  options={[
                    {
                      label: HOUSE,
                      meta: 'Single detached house',
                      value: HOUSE,
                    },
                    {
                      label: TOWNHOUSE,
                      meta: 'Multi-level home',
                      value: TOWNHOUSE,
                    },
                    {
                      label: 'Condo',
                      meta: 'Single unit in a building',
                      value: CONDO,
                    },
                    {
                      label: MULTIPLEX,
                      meta: '(duplex to 24 units)',
                      value: MULTIPLEX,
                    },
                    {
                      label: COMMUNITY,
                      meta: '(25+ units)',
                      value: COMMUNITY,
                    },
                  ]}
                  onChange={handleUpdatePropertyType}
                />
              </div>
            ) : (
              <Select
                css={{ color: 'text.subtle', whiteSpace: 'pre' }}
                aria-label="Property type"
                value={propertyType || 'Please select'}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  handleUpdatePropertyType(e.target.value);
                }}
                disabled={isSubmitDisabled}
                data-testid="propertyType-select"
              >
                <option disabled={true}>Please select</option>
                <option disabled={isDisabledForCommunityType} value={HOUSE}>
                  {HOUSE}
                </option>
                <option disabled={isDisabledForCommunityType} value={CONDO}>
                  {CONDO}
                </option>
                <option disabled={isDisabledForCommunityType} value={TOWNHOUSE}>
                  {TOWNHOUSE}
                </option>
                <option disabled={isDisabledForNonCommunityType} value={COMMUNITY}>
                  {COMMUNITY}
                </option>
              </Select>
            )}

            <FormHelp data-testid="propertyType-error" error={!!errorObj.propertyType}>
              {errorObj.propertyType}
              {errorObj.propertyType === MULTI_FAMILY_RESTRICTED_ERROR_MESSAGE && (
                <>
                  <br />
                  <span>
                    You can also contact&nbsp;
                    <Anchor href={zillowAdvertisingURL} target="_blank">
                      Zillow advertising
                    </Anchor>
                    &nbsp;to post this listing as an apartment community.
                  </span>
                </>
              )}
            </FormHelp>
          </FormField>
        </Box>

        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>{renderUnitFormField()}</Box>
      </Box>

      {(!finalizedisRoomForRentValue || isCommunityType) && contracts.length > 1 && (
        <FormField css={{ marginBlock: '300' }} error={!!errorObj.customerId} data-testid="active-contracts">
          <Label>Active Contracts</Label>
          <Select
            css={{ color: 'text.subtle', whiteSpace: 'pre' }}
            aria-label="Active contracts"
            value={customerId || 'Please select'}
            onChange={(e) => handleUpdateCustomerId(e as unknown as ChangeEvent<HTMLInputElement>)}
            disabled={isSubmitDisabled}
          >
            <option disabled={true}>Please select</option>
            {contracts.map((contract) => (
              <option key={contract.customerId} value={contract.customerId}>
                {contract.name}
              </option>
            ))}
          </Select>
          <FormHelp error={!!errorObj.customerId}>{errorObj.customerId}</FormHelp>
        </FormField>
      )}

      {!isMultiplexType && !isCommunityType && behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.ADD && (
        <RoomForRentCheckbox
          isRoomForRentChecked={finalizedisRoomForRentValue}
          isRoomForRentDisabled={isRoomForRentDisabled}
          toggleRoomForRentChecked={handleToggleRoomForRent}
        />
      )}
    </div>
  );
};

export default AddPropertyForm;
